import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { findYear, holidayAvailable, linkFor, runningIn, spellOut, whenText } from "../../../constants/catalogue";
import { get_current_period } from "../../../apis/Branches";

// "What is there for my child, and where?"
//
// Four steps, in the order a parent actually decides:
//
//   Year level  ->  Course  ->  How they attend
//
// Each step only appears once the one before it is answered, so the card is
// never a wall of empty dropdowns. Year alone already produces a useful
// answer; the rest narrows it down.
//
// Campus is deliberately NOT asked here. Choosing a campus and then being
// shown "Enrol now" implies you can pick that campus's class time, and that
// does not exist yet — the campus timetable lives on the course page.

import { C, FONT } from "../../../constants/mq";

const BRAND = C.red;

// Term and holiday courses run differently and are chosen separately.
// Online is not open yet — it is listed as coming rather than offered,
// because sending a parent down a path we cannot deliver is worse than
// leaving it out.
// Years are shown in their two stages: as one flat row Year 12 dropped onto a
// line of its own and looked like an afterthought.
const YEAR_GROUPS = [
  { label: "Primary", years: [1, 2, 3, 4, 5, 6] },
  { label: "High School", years: [7, 8, 9, 10, 11, 12] },
];

// A holiday course is ten lessons run daily in the break, not a shorter term,
// so it is described that way. It is only offered on some courses — see
// holidayAvailable — and the chip is left out rather than shown greyed, since
// a disabled option still reads as something we run.
const METHODS = [
  { key: "term", label: "Term course", note: "Weekly through the school term" },
  { key: "holiday", label: "Holiday course", note: "10 lessons, daily in the break" },
];

// Styled as the "Explore an area of study" panel on mq.edu.au: a sand box
// that overlaps the bottom of the hero, with year levels and courses as rows
// that end in a chevron.
const styles = {
  // transparent, so the hero shows on either side of the panel where it
  // overlaps (a white section here hid the hero's bottom edge instead)
  section: { background: "transparent", padding: "0 0 28px", position: "relative", zIndex: 2, fontFamily: FONT },
  container: { maxWidth: 1072, margin: "0 auto", padding: "0 24px" },
  card: {
    background: C.sand,
    borderRadius: 4,
    padding: "28px 28px 24px",
    marginTop: -64,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
  heading: { fontSize: 19, fontWeight: 700, color: C.ink, margin: "0 0 4px" },
  sub: { fontSize: 14, color: C.body, margin: "0 0 18px", lineHeight: 1.6 },

  stepRow: { marginBottom: 14 },
  yearGroup: { marginBottom: 4 },
  yearGroupLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: C.muted,
    margin: "6px 0 0",
  },
  stepLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11.5,
    fontWeight: 700,
    color: C.ink,
    margin: "0 0 2px",
    letterSpacing: "1.2px",
  },
  stepNo: {
    width: 20,
    height: 20,
    borderRadius: 999,
    background: C.bright,
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    letterSpacing: 0,
  },
  rows: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
    columnGap: 36,
  },
  rowNote: { display: "block", fontSize: 12, fontWeight: 400, color: C.muted, marginTop: 2 },

  result: {
    marginTop: 14,
    paddingTop: 18,
    borderTop: `1px solid ${C.line}`,
  },
  resultHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  periodNote: { fontSize: 12.5, color: C.muted, lineHeight: 1.6, margin: "12px 0 0" },
  resultTitle: { fontSize: 15, fontWeight: 700, color: C.ink, margin: 0 },
  resultCount: { fontSize: 13, color: C.muted },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
    gap: 12,
  },
  item: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "14px 16px",
    minHeight: 64,
  },
  itemName: { display: "block", fontSize: 14, fontWeight: 600, lineHeight: 1.35, color: C.ink },
  itemMeta: { display: "block", fontSize: 12, color: C.muted, marginTop: 3 },
  chev: { color: BRAND, fontSize: 20, lineHeight: 1, flexShrink: 0 },

  summary: {
    background: "#ffffff",
    border: `1px solid ${C.line}`,
    borderRadius: 4,
    padding: "14px 16px 14px 20px",
    marginTop: 14,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
  },
  summaryText: { fontSize: 14, color: C.body, margin: 0, lineHeight: 1.6 },
  summaryStrong: { color: C.ink, fontWeight: 700 },
  empty: {
    border: `1px dashed ${C.line}`,
    borderRadius: 4,
    padding: "24px 16px",
    textAlign: "center",
    fontSize: 14,
    color: C.muted,
    background: "#ffffff",
  },
  reset: {
    background: "none",
    border: "none",
    color: BRAND,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
    marginTop: 14,
  },
  links: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 12,
    marginTop: 18,
    paddingTop: 18,
    borderTop: `1px solid ${C.line}`,
  },
};

// six years per stage, so each stage is one row on desktop
const yearCss = `
.cs-year-rows { grid-template-columns: repeat(6, 1fr) !important; }
@media (max-width: 900px) { .cs-year-rows { grid-template-columns: repeat(3, 1fr) !important; } }
@media (max-width: 480px) { .cs-year-rows { grid-template-columns: repeat(2, 1fr) !important; } }
`;

const CourseFinder = () => {
  const [year, setYear] = useState("");
  const [subject, setSubject] = useState("");
  const [method, setMethod] = useState("");

  const [period, setPeriod] = useState(null);
  useEffect(() => {
    let alive = true;
    get_current_period().then((p) => alive && setPeriod(p));
    return () => {
      alive = false;
    };
  }, []);

  const yearEntry = useMemo(() => findYear(year), [year]);
  // Only what runs now. Year 6 changes course through the year (WRMT, WEM,
  // Selective, High School Preparation); its other courses are listed on
  // each course page under Related courses.
  const running = useMemo(() => runningIn(year, period?.key), [year, period]);
  const subjects = yearEntry ? running.subjects : [];
  const allCount = yearEntry?.subjects.length || 0;
  const chosenSubject = subjects.find((s) => s.slug === subject);
  // The course's name at this year level (WEM in Year 6), as on its page.
  const shown = (sub) => sub.label || sub.name;
  const methods = useMemo(
    () =>
      METHODS.filter(
        (m) =>
          m.key !== "holiday" ||
          (chosenSubject && holidayAvailable(year, chosenSubject.slug))
      ),
    [year, chosenSubject]
  );

  // With no holiday option there is nothing to choose, so step 3 is skipped
  // and the term course is taken as read — the parent still gets the summary
  // and the enrol button rather than a dead end.
  const chosenMethod = methods.length === 1 ? methods[0].key : method;

  const reset = () => {
    setYear("");
    setSubject("");
    setMethod("");
  };

  const pickYear = (y) => {
    setYear(String(y));
    setSubject("");
    setMethod("");
  };

  const Step = ({ no, label, children }) => (
    <div style={styles.stepRow}>
      <p style={styles.stepLabel}>
        <span style={styles.stepNo}>{no}</span>
        {label}
      </p>
      {children}
    </div>
  );

  const row = (on) => `mq-row${on ? " mq-row--on" : ""}`;

  return (
    <section style={styles.section}>
      <style>{yearCss}</style>
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.heading}>Find a course</h2>
          <p style={styles.sub}>
            Start with your child&rsquo;s year level. Each step narrows it down
            to the class you can enrol in.
          </p>

          <Step no={1} label="YEAR LEVEL">
            {YEAR_GROUPS.map((g) => (
              <div key={g.label} style={styles.yearGroup}>
                <span style={styles.yearGroupLabel}>{g.label}</span>
                <div className="cs-year-rows" style={styles.rows}>
                  {g.years.map((y) => (
                    <button
                      key={y}
                      type="button"
                      className={row(String(y) === year)}
                      onClick={() => pickYear(y)}
                    >
                      Year {y}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </Step>

          {yearEntry && (
            <Step no={2} label="COURSE">
              <div style={styles.rows}>
                {subjects.map((sub) => (
                  <button
                    key={sub.slug}
                    type="button"
                    className={row(sub.slug === subject)}
                    onClick={() => {
                      setSubject(sub.slug);
                      setMethod("");
                    }}
                  >
                    <span>
                      {shown(sub)}
                      {(spellOut(shown(sub)) || sub.when?.length) && (
                        <span style={styles.rowNote}>
                          {[spellOut(shown(sub)), whenText(sub.when)].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </Step>
          )}

          {chosenSubject && methods.length > 1 && (
            <Step no={3} label="HOW THEY ATTEND">
              <div style={styles.rows}>
                {methods.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    className={row(m.key === method)}
                    onClick={() => setMethod(m.key)}
                  >
                    <span>
                      {m.label}
                      <span style={styles.rowNote}>{m.note}</span>
                    </span>
                  </button>
                ))}
              </div>
            </Step>
          )}

          {/* Results appear as soon as a year is chosen — a parent who only
              knows the year still gets an answer. */}
          {yearEntry && (
            <div style={styles.result}>
              <div style={styles.resultHead}>
                <h3 style={styles.resultTitle}>
                  {chosenSubject
                    ? `Year ${year} ${shown(chosenSubject)}`
                    : running.period
                      ? `Year ${year} programs in ${running.period.label}`
                      : `Year ${year} programs`}
                </h3>
                <span style={styles.resultCount}>
                  {chosenSubject ? 1 : subjects.length}{" "}
                  {(chosenSubject ? 1 : subjects.length) === 1
                    ? "course"
                    : "courses"}
                </span>
              </div>

              {subjects.length === 0 ? (
                <div style={styles.empty}>
                  Nothing is listed for that year yet. Contact us and we will
                  tell you what is running.
                </div>
              ) : (
                <div style={styles.grid}>
                  {(chosenSubject ? [chosenSubject] : subjects).map((sub) => {
                    const target = linkFor(Number(year), sub.name);
                    return (
                      <Link key={sub.slug} to={target.href} className="mq-card" style={styles.item}>
                        <span>
                          <span style={styles.itemName}>{shown(sub)}</span>
                          <span style={styles.itemMeta}>
                            Year {year}
                            {sub.when?.length ? ` · ${whenText(sub.when)}` : ""}
                            {spellOut(shown(sub)) && ` · ${spellOut(shown(sub))}`}
                            {sub.parts?.length ? ` · ${sub.parts.map((p) => p.name).join(", ")}` : ""}
                          </span>
                        </span>
                        <span style={styles.chev}>&rsaquo;</span>
                      </Link>
                    );
                  })}
                </div>
              )}

              {running.period && subjects.length < allCount && (
                <p style={styles.periodNote}>
                  Year {year} courses change through the year. These are the ones
                  running in {running.period.label}; the rest are on each course
                  page under Related courses.
                </p>
              )}

              {chosenSubject && chosenMethod && (
                <div style={styles.summary}>
                  <p style={styles.summaryText}>
                    <span style={styles.summaryStrong}>
                      Year {year} {shown(chosenSubject)}
                    </span>{" "}
                    &middot; {methods.find((m) => m.key === chosenMethod)?.label}
                  </p>
                  <Link className="mq-btn" to="/enroll">
                    Enrol now
                  </Link>
                </div>
              )}

              <button type="button" style={styles.reset} onClick={reset}>
                Start again
              </button>
            </div>
          )}

          <div style={styles.links}>
            <Link className="mq-link" to="/enroll">How to enrol</Link>
            <Link className="mq-link" to="/pricing">Pricing</Link>
            <Link className="mq-link" to="/our-courses">All courses</Link>
            <Link className="mq-link" to="/our-branches">Campuses</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CourseFinder;
